import { gql } from "@apollo/client";
import * as Sentry from "@sentry/react";

import { isNullable } from "src/utils/types/types";
import { useUpdateQueryScheduleMutation } from "src/gql/codegen/graphql";
import { getRefetchQueryScheduleOptions } from ".";

import type { SessionWithUser } from "src/utils/users/types";
import type { FormState } from "shared/ScheduleExecutionModalContent";

export function useUpdateQuerySchedule() {
  const [callUpdateQueryScheduleMutation, updateQueryScheduleResult] =
    useUpdateQueryScheduleMutation();

  function updateQuerySchedule(
    session: SessionWithUser,
    args: { queryId: number; scheduleId: string },
    form: FormState
  ): Promise<void> {
    if (isNullable(session) || updateQueryScheduleResult.loading) {
      return new Promise(() => {});
    }

    return new Promise((resolve) => {
      callUpdateQueryScheduleMutation({
        context: { session },
        variables: {
          cron_job_id: args.scheduleId,
          cron_expression: form.cronString,
          performance: form.performanceTier,
        },
        refetchQueries: [getRefetchQueryScheduleOptions(session, args.queryId)],
      }).then(
        () => {
          resolve();
        },
        (error) => {
          error.message = "Failed to update query schedule: " + error.message;
          Sentry.captureException(error);
        }
      );
    });
  }

  return [updateQuerySchedule, updateQueryScheduleResult] as const;
}

gql`
  mutation UpdateQuerySchedule(
    $cron_job_id: String!
    $cron_expression: String!
    $performance: String!
  ) {
    update_query_schedule(
      cron_job_id: $cron_job_id
      cron_expression: $cron_expression
      performance: $performance
    ) {
      query_id
    }
  }
`;
